import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AUTH_CONFIG } from '../features/auth/auth.config';
import { TokenService, TokenResponse } from './token.service';
import { AuthEventsService } from './auth-events.service';
import { ExchangeCategoryService } from './exchange-category.service';
import { NetworkMetadataService } from './network-metadata.service';

export interface MfaStatusResponse {
  is2FAEnabled: boolean;
  username: string;
}

export interface BackendAuthError {
  message: string;
  remainingAttempts: number;
}

export function authErrorMessage(error: BackendAuthError): string {
  if (!error.remainingAttempts) return error.message;
  const plural = error.remainingAttempts === 1 ? '' : 's';
  return `${error.message} You have ${error.remainingAttempts} attempt${plural} left.`;
}

interface TokenPayload {
  sub: string;
  preferred_username?: string;
  email?: string;
  name?: string;
  profile_guid?: string;
  authorization?: { permissions?: Permission[] };
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] }>;
}

export interface PendingLogin {
  username: string;
  password: string;
}

export interface Permission {
  rsid: string;
  rsname: string;
  scopes?: string[];
}

export interface UserProfile {
  username: string;
  email?: string;
  name?: string;
  roles?: string[];
  permissions?: Permission[];
  profile_guid?: string | null;
}

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  private http = inject(HttpClient);
  private router = inject(Router);
  private tokenService = inject(TokenService);
  private authEvents = inject(AuthEventsService);
  private exchangeCategoryService = inject(ExchangeCategoryService);

  private cfg = inject(AUTH_CONFIG);
  private readonly metadata = inject(NetworkMetadataService);

  private currentUserSubject = new BehaviorSubject<UserProfile | null>(null);

  public currentUser$ = this.currentUserSubject.asObservable();

  private authApiUrl = `${this.cfg.baseUrl}${this.cfg.endpoints.auth.base}`;
  private accountApiUrl = `${this.cfg.baseUrl}${this.cfg.endpoints.account.base}`;

  private refreshTokenTimeout: ReturnType<typeof setTimeout> | null = null;
  private refreshExpirationTimeout: ReturnType<typeof setTimeout> | null = null;
  private pendingLoginSubject = new BehaviorSubject<PendingLogin | null>(null);
  private redirectUrl: string | null = null;

  private isLoggedOut = false;

  private tokensSub?: Subscription;
  private logoutEventSub?: Subscription;

  constructor() {
    this.loadStoredUser();

    this.tokensSub = this.tokenService.tokens$.subscribe((snap) => {
      if (this.isLoggedOut) return;

      if (snap.accessToken) {
        this.startRefreshTokenTimer();
        this.startRefreshExpirationTimer();
        this.exchangeCategoryService.getExchangeCategories().subscribe();

        try {
          const userInfo = this.getUserInfoFromToken(snap.accessToken);
          this.currentUserSubject.next(userInfo);
        } catch (err) {
          console.warn(err);
        }
      } else {
        this.currentUserSubject.next(null);
      }
    });

    this.logoutEventSub = this.authEvents.logout$.subscribe(() => {
      this.logout();
    });
  }

  ngOnDestroy(): void {
    this.tokensSub?.unsubscribe();
    this.logoutEventSub?.unsubscribe();
    this.stopRefreshTokenTimer();
    this.clearRefreshExpirationTimer();
  }

  private loadStoredUser(): void {
    const token = this.tokenService.getAccessToken();
    if (token) {
      try {
        const userInfo = this.getUserInfoFromToken(token);
        this.currentUserSubject.next(userInfo);
        this.startRefreshTokenTimer();
        this.startRefreshExpirationTimer();
        this.exchangeCategoryService.getExchangeCategories().subscribe();
        this.metadata.load();
      } catch (error) {
        console.error('Error loading stored user:', error);
        this.logout();
      }
    }
  }

  checkMfaStatus(username: string, password: string): Observable<MfaStatusResponse> {
    return this.http
      .post<MfaStatusResponse>(`${this.authApiUrl}${this.cfg.endpoints.auth.mfaStatus}`, {
        username,
        password,
      })
      .pipe(
        catchError((error) => {
          const backend = error?.error as BackendAuthError;
          const message = backend.message;
          const remainingAttempts = backend.remainingAttempts;
          const status = error?.status;
          return throwError(() => ({ message, remainingAttempts, status }));
        }),
      );
  }

  setPendingLogin(username: string, password: string): void {
    this.pendingLoginSubject.next({ username, password });
  }

  getPendingLogin(): PendingLogin | null {
    return this.pendingLoginSubject.value;
  }

  clearPendingLogin(): void {
    this.pendingLoginSubject.next(null);
  }

  setRedirectUrl(url: string): void {
    this.redirectUrl = url;
  }

  consumeRedirectUrl(): string | null {
    const url = this.redirectUrl;
    this.redirectUrl = null;
    return url;
  }

  loginWithMfa(username: string, password: string, totp: string): Observable<UserProfile> {
    this.isLoggedOut = false;
    return this.http
      .post<TokenResponse>(`${this.authApiUrl}${this.cfg.endpoints.auth.login}`, {
        username,
        password,
        totp,
      })
      .pipe(
        tap((response) => {
          this.setSession(response);
          this.clearPendingLogin();
          this.metadata.load();
        }),
        map((response) => {
          const userInfo = this.getUserInfoFromToken(response.access_token);
          this.currentUserSubject.next(userInfo);
          return userInfo;
        }),
        catchError((error) => {
          const backend = error?.error as BackendAuthError;
          const message = backend.message;
          const remainingAttempts = backend.remainingAttempts;
          return throwError(() => ({ message, remainingAttempts }));
        }),
      );
  }

  logout(): void {
    const refreshToken = this.tokenService.getRefreshToken();
    const accessToken = this.tokenService.getAccessToken();

    if (refreshToken && accessToken) {
      const headers = new HttpHeaders({ Authorization: `Bearer ${accessToken}` });

      this.http
        .post<{ message: string }>(`${this.authApiUrl}${this.cfg.endpoints.auth.logout}`, { refreshToken }, { headers })
        .pipe(
          catchError((error) => {
            console.warn('Backend logout failed:', error);
            return throwError(() => error);
          }),
        )
        .subscribe({
          next: () => this.performLocalLogout(),
          error: () => this.performLocalLogout(),
        });
    } else {
      this.performLocalLogout();
    }
  }

  private performLocalLogout(): void {
    this.isLoggedOut = true;
    this.stopRefreshTokenTimer();
    this.clearRefreshExpirationTimer();

    this.tokenService.clear();
    this.metadata.clear();
    this.currentUserSubject.next(null);

    this.router.navigate(['/login'], { replaceUrl: true });
  }

  refreshToken(): Observable<TokenResponse> {
    if (!this.tokenService.getRefreshToken()) {
      return throwError(() => new Error('No refresh token available'));
    }
    if (!this.tokenService.getAccessToken()) {
      return throwError(() => new Error('No access token available'));
    }

    return this.tokenService.refresh().pipe(
      tap((response) => this.setSession(response)),
      catchError((error) => {
        console.error('Token refresh error:', error);
        this.authEvents.requestLogout(true);
        return throwError(() => ({
          message: 'Session expired. Please login again.',
          status: error?.status,
        }));
      }),
    );
  }

  getAccessToken(): string | null {
    return this.tokenService.getAccessToken();
  }

  isLoggedIn(): boolean {
    const token = this.tokenService.getAccessToken();
    const expiration = this.tokenService.getAccessExp();
    if (!token || !expiration) return false;
    return Date.now() < expiration;
  }

  activateUser(token: string): Observable<{
    success: boolean;
    twoFactorAuth: {
      secret: string;
      qrCode: string;
    };
  }> {
    const url = `${this.accountApiUrl}${this.cfg.endpoints.account.activateUser}`;

    return this.http
      .post<{
        success: boolean;
        twoFactorAuth: {
          secret: string;
          qrCode: string;
        };
      }>(url, null, {
        params: { token },
      })
      .pipe(
        catchError(() => {
          return throwError(() => new Error('User activation failed'));
        }),
      );
  }

  consumeMagicLink(token: string): Observable<{ success: boolean }> {
    const url = `${this.accountApiUrl}${this.cfg.endpoints.account.consumeMagicLink}`;

    return this.http
      .post<{ success: boolean }>(url, null, {
        params: { token },
      })
      .pipe(
        catchError(() => {
          return throwError(() => new Error('Failed to consume magic link'));
        }),
      );
  }

  validateTotpReset(data: { token: string; username: string; password: string }): Observable<{
    success: boolean;
    wrongEmailProvided?: boolean;
    wrongCredentials?: boolean;
    remainingAttempts?: number;
    twoFactorAuth?: {
      secret: string;
      qrCode: string;
    };
  }> {
    const url = `${this.accountApiUrl}${this.cfg.endpoints.account.validateTotpReset}`;

    return this.http
      .post<{
        success: boolean;
        wrongEmailProvided?: boolean;
        wrongCredentials?: boolean;
        remainingAttempts?: number;
        twoFactorAuth?: {
          secret: string;
          qrCode: string;
        };
      }>(url, data)
      .pipe(
        catchError(() => {
          return throwError(() => new Error('Failed to validate TOTP reset'));
        }),
      );
  }

  completeActivation(data: { token: string; password: string; totp: string }): Observable<{
    success: boolean;
    remainingAttempts?: number;
  }> {
    const url = `${this.accountApiUrl}${this.cfg.endpoints.account.completeActivation}`;

    return this.http
      .post<{
        success: boolean;
        remainingAttempts?: number;
      }>(url, data)
      .pipe(
        catchError((error) => {
          console.error('Complete activation error:', error);
          return throwError(() => ({
            message: 'Activation failed',
            status: error?.status,
            body: error?.error,
          }));
        }),
      );
  }

  completePasswordReset(data: { token: string; password: string }): Observable<{
    success: boolean;
  }> {
    const url = `${this.accountApiUrl}${this.cfg.endpoints.account.completePasswordReset}`;

    return this.http
      .post<{
        success: boolean;
      }>(url, data)
      .pipe(
        catchError((error) => {
          console.error('Complete password reset error:', error);
          return throwError(() => ({
            message: 'Password reset failed',
            status: error?.status,
          }));
        }),
      );
  }

  completeTotpReset(data: { token: string; totp: string }): Observable<{
    success: boolean;
    remainingAttempts?: number;
  }> {
    const url = `${this.accountApiUrl}${this.cfg.endpoints.account.completeTotpReset}`;

    return this.http.post<{ success: boolean; remainingAttempts?: number }>(url, data).pipe(
      catchError((error) => {
        console.error('Complete TOTP reset error:', error);
        return throwError(() => ({
          message: 'TOTP reset failed',
          status: error?.status,
          body: error?.error,
        }));
      }),
    );
  }

  public setSession(response: TokenResponse): void {
    if (this.isLoggedOut) return;
    this.tokenService.setTokens(response);
    this.exchangeCategoryService.getExchangeCategories().subscribe();
    // Timers re-arm via the tokens$ subscription
  }

  private startRefreshTokenTimer(): void {
    if (this.isLoggedOut) return;
    this.stopRefreshTokenTimer();

    const expiresAt = this.tokenService.getAccessExp();
    if (!expiresAt) {
      this.authEvents.requestLogout(true);
      return;
    }

    const timeout = expiresAt - Date.now() - 30 * 1000;
    if (timeout > 0) {
      this.refreshTokenTimeout = setTimeout(() => this.refreshToken().subscribe(), timeout);
    } else {
      this.refreshToken().subscribe();
    }
  }

  private stopRefreshTokenTimer(): void {
    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
      this.refreshTokenTimeout = null;
    }
  }

  private startRefreshExpirationTimer(): void {
    this.clearRefreshExpirationTimer();

    const expiresAt = this.tokenService.getRefreshExp();
    if (!expiresAt) return;

    const msUntilLogout = expiresAt - Date.now() - 30 * 1000;
    if (msUntilLogout > 0) {
      this.refreshExpirationTimeout = setTimeout(() => {
        this.authEvents.requestLogout(true);
      }, msUntilLogout);
    } else {
      this.authEvents.requestLogout(true);
    }
  }

  private clearRefreshExpirationTimer(): void {
    if (this.refreshExpirationTimeout) {
      clearTimeout(this.refreshExpirationTimeout);
      this.refreshExpirationTimeout = null;
    }
  }

  private getUserInfoFromToken(token: string): UserProfile {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(''),
      );

      const payload: TokenPayload = JSON.parse(jsonPayload);

      return {
        username: payload.preferred_username || payload.sub,
        email: payload.email,
        name: payload.name,
        roles: this.extractRoles(payload),
        permissions: this.extractPermissions(payload),
        profile_guid: payload.profile_guid || null,
      };
    } catch (error) {
      console.error('Error decoding token:', error);
      throw new Error('Invalid token format', { cause: error });
    }
  }

  private extractPermissions(payload: TokenPayload): Permission[] {
    const permissions: Permission[] = [];

    if (payload.authorization && payload.authorization.permissions) {
      permissions.push(...payload.authorization.permissions);
    }

    return permissions;
  }

  private extractRoles(payload: TokenPayload): string[] {
    const roles: string[] = [];

    if (payload.realm_access && payload.realm_access.roles) {
      roles.push(...payload.realm_access.roles);
    }

    if (payload.resource_access) {
      Object.keys(payload.resource_access).forEach((resource) => {
        const resourceRoles = payload.resource_access?.[resource].roles;
        if (resourceRoles) {
          roles.push(...resourceRoles);
        }
      });
    }

    return roles;
  }

  hasPermission(resourceName: string, scope?: string): boolean {
    const user = this.currentUserSubject.value;
    if (!user?.permissions) return false;

    const permission = user.permissions.find((p) => p.rsname === resourceName);
    if (!permission) return false;

    if (!scope) return true;

    if (!permission.scopes) return false;

    return permission.scopes.includes(scope);
  }
}
